/**
 * Browser-side activity recorder: what the person clicks and where they go, attributed to the
 * work item they have active (or none). Batched and posted to /resources/work as
 * `intent=activity`; flushed every few seconds, when the batch is big, and on page hide
 * (sendBeacon so nothing is lost on close).
 *
 * We record labels of interactive elements (button/link text, aria-label, name), never input
 * values.
 */

export type ActivityEvent = {
	kind: 'ui.click' | 'app.navigate'
	route: string
	label?: string | null
	item_id?: number | null
	detail?: Record<string, string>
	at: string
}

const MAX_BATCH = 25
const FLUSH_MS = 4000
const INTERACTIVE =
	'a,button,[role="button"],[role="tab"],[role="menuitem"],summary,select,input[type="submit"],input[type="checkbox"],input[type="radio"],label'

let buffer: Array<ActivityEvent> = []
let timer: ReturnType<typeof setTimeout> | null = null
let activeItemId: number | null = null
let installed = false

export function setActiveItemForActivity(id: number | null) {
	activeItemId = id
}

function post(events: Array<ActivityEvent>, beacon = false) {
	if (!events.length) return
	const fd = new FormData()
	fd.set('intent', 'activity')
	fd.set('events', JSON.stringify(events))
	if (beacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
		navigator.sendBeacon('/resources/work', fd)
		return
	}
	void fetch('/resources/work', {
		method: 'POST',
		body: fd,
		keepalive: true,
	}).catch(() => {})
}

export function flushActivity(beacon = false) {
	if (timer) {
		clearTimeout(timer)
		timer = null
	}
	const batch = buffer
	buffer = []
	post(batch, beacon)
}

function schedule() {
	if (buffer.length >= MAX_BATCH) return flushActivity()
	if (!timer) timer = setTimeout(() => flushActivity(), FLUSH_MS)
}

export function recordActivity(
	ev: Omit<ActivityEvent, 'at' | 'item_id'> & { item_id?: number | null },
) {
	buffer.push({
		...ev,
		item_id: ev.item_id === undefined ? activeItemId : ev.item_id,
		at: new Date().toISOString(),
	})
	schedule()
}

function labelFor(el: Element): string {
	const aria = el.getAttribute('aria-label') || el.getAttribute('title')
	if (aria) return aria.trim().slice(0, 120)
	const text = (el.textContent || '').replace(/\s+/g, ' ').trim()
	if (text) return text.slice(0, 120)
	const name =
		el.getAttribute('name') ||
		el.getAttribute('placeholder') ||
		el.getAttribute('id')
	return (name || el.tagName.toLowerCase()).slice(0, 120)
}

/** Install the document-level click listener once; returns an uninstaller. */
export function installActivityRecorder(): () => void {
	if (installed || typeof document === 'undefined') return () => {}
	installed = true
	const onClick = (e: MouseEvent) => {
		const target = e.target as Element | null
		const el = target?.closest?.(INTERACTIVE) as HTMLElement | null
		if (!el) return
		const detail: Record<string, string> = { tag: el.tagName.toLowerCase() }
		const href = el.getAttribute('href')
		if (href) detail.href = href.slice(0, 300)
		const name = el.getAttribute('name')
		if (name) detail.name = name.slice(0, 80)
		const section = el.closest('aside,nav,header,main,dialog,form')
		if (section)
			detail.section =
				section.tagName.toLowerCase() +
				(section.getAttribute('aria-label')
					? `:${section.getAttribute('aria-label')}`
					: '')
		recordActivity({
			kind: 'ui.click',
			route: location.pathname + location.search,
			label: labelFor(el),
			detail,
		})
	}
	const onHide = () => flushActivity(true)
	document.addEventListener('click', onClick, true)
	window.addEventListener('pagehide', onHide)
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'hidden') flushActivity(true)
	})
	return () => {
		document.removeEventListener('click', onClick, true)
		window.removeEventListener('pagehide', onHide)
		installed = false
	}
}
